/** Fork choice popup — pauses gameplay, presents fork options or node attainment announcements. */
import Phaser from 'phaser';
import type { GameScene } from './GameScene';
import type { ForkEvent } from '../components/SkillProgressionComponent';
import { ConfigLoader } from '../systems/ConfigLoader';
import { crispText, crispButton, crispRect, setupCrispCamera, CRISP_SCALE } from '../ui/CrispUtil';
import { Font, Hex, ButtonColor, Color } from '../ui/Theme';
import { GAME_WIDTH, GAME_HEIGHT } from '../utils/Constants';
import { NODE_TYPE_LABELS } from '../utils/Types';

const CARD_GAP = 10;
const CARD_PAD = 8;

/** Title text for attainment popups, keyed by node type. */
const ATTAINED_TITLES: Record<string, string> = {
  active: 'NEW ACTIVE SKILL',
  evolution: 'SKILL EVOLVED',
  upgrade: 'NEW UPGRADE',
  passive: 'NEW PASSIVE',
};

type ForkChoiceInitData =
  | (ForkEvent & { mode?: 'fork' })
  | { mode: 'attained'; treeName: string; nodeId: string; nodeType: string };

/**
 * Modal scene launched over a paused GameScene. Operates in two modes:
 * - **fork**: presents 2-3 skill options at a branch point (player must choose)
 * - **attained**: single-card celebration when a node unlocks (click/ESC to dismiss)
 *
 * A static queue drains multiple attainment popups in sequence before resuming gameplay.
 */
export class ForkChoiceScene extends Phaser.Scene {
  private mode: 'fork' | 'attained' = 'fork';
  private forkEvent!: ForkEvent;
  private attainedData!: { treeName: string; nodeId: string; nodeType: string };
  private objects: Phaser.GameObjects.GameObject[] = [];

  /** Shared cycling state for debug B-key iteration across all forks. */
  private static allForks: { treeName: string; forkId: string; options: string[] }[] = [];
  private static forkIndex = 0;

  /** Queue for attainment popups — multiple nodes can unlock in rapid succession. */
  private static unlockQueue: { treeName: string; nodeId: string; nodeType: string }[] = [];

  /** Adds a node-attainment event to the static unlock queue for sequential presentation. */
  static enqueue(evt: { treeName: string; nodeId: string; nodeType: string }): void {
    ForkChoiceScene.unlockQueue.push({ treeName: evt.treeName, nodeId: evt.nodeId, nodeType: evt.nodeType });
  }

  /** Returns true if there are attainment popups waiting in the queue. */
  static hasQueued(): boolean {
    return ForkChoiceScene.unlockQueue.length > 0;
  }

  /** Removes and returns the next attainment event from the front of the queue. */
  static dequeue(): { treeName: string; nodeId: string; nodeType: string } | undefined {
    return ForkChoiceScene.unlockQueue.shift();
  }

  constructor() {
    super({ key: 'ForkChoiceScene' });
  }

  /** Reads the launch data and sets the operating mode (fork or attained) before create() runs. */
  init(data: ForkChoiceInitData): void {
    if (data.mode === 'attained') {
      this.mode = 'attained';
      this.attainedData = data;
    } else {
      this.mode = 'fork';
      this.forkEvent = data as ForkEvent;
    }
  }

  /**
   * Builds the modal UI: backdrop, title, flavor text, attunement label, and one card per option.
   * Card click behavior differs by mode — fork cards commit a choice, attained cards dismiss.
   */
  create(): void {
    setupCrispCamera(this);

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;

    // Semi-transparent backdrop — click to dismiss in attained mode
    const backdrop = crispRect(this, cx, cy, GAME_WIDTH, GAME_HEIGHT, Color.black, 0.7)
      .setInteractive().setDepth(0);
    if (this.mode === 'attained') {
      backdrop.on('pointerdown', () => this.dismiss());
    }
    this.objects.push(backdrop);

    const treeName = this.mode === 'fork' ? this.forkEvent.treeName : this.attainedData.treeName;
    const options = this.mode === 'fork' ? this.forkEvent.options : [this.attainedData.nodeId];

    // Title — type-dependent in attained mode, fixed in fork mode
    const titleText = this.mode === 'fork'
      ? 'CHOOSE YOUR PATH'
      : (ATTAINED_TITLES[this.attainedData.nodeType] ?? 'NEW SKILL');
    const title = crispText(this, cx, 36, titleText, {
      fontFamily: Font.family, fontSize: Font.sizeTitle, color: Hex.textPrimary,
    }).setOrigin(0.5).setDepth(1);
    this.objects.push(title);

    // Card layout — compact cards centered vertically (single cards halved to avoid spanning the full screen)
    let cardWidth = Math.floor((GAME_WIDTH - 100 - (options.length - 1) * CARD_GAP) / options.length);
    if (options.length === 1) cardWidth = Math.floor(cardWidth / 2);
    const cardHeight = 320;
    const cardTop = Math.floor(cy - cardHeight / 2 + 30);
    const cardBottom = cardTop + cardHeight;
    const totalWidth = options.length * cardWidth + (options.length - 1) * CARD_GAP;

    // Flavor text below title — random line from the tree's fork flavor pool
    const tree = ConfigLoader.v4Trees[treeName];
    const flavorPool = tree?.forkFlavorText ?? [];
    const flavorLine = flavorPool.length > 0
      ? flavorPool[Math.floor(Math.random() * flavorPool.length)]
      : `Your ${treeName} attunement has reached a turning point.`;
    const titleFlavor = crispText(this, cx, 54, flavorLine, {
      fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.textSecondary,
      wordWrap: { width: totalWidth * CRISP_SCALE },
      align: 'center',
    }).setOrigin(0.5, 0).setDepth(1);
    this.objects.push(titleFlavor);

    // Attunement name + icon — just above the cards, matching AttunementTreeScene sizing
    const iconSize = 28;
    const iconGap = 8;
    const iconSpace = (iconSize + iconGap) / 2;
    const attnY = cardTop - 22;

    const attnLabel = crispText(this, cx + iconSpace, attnY, treeName.toUpperCase(), {
      fontFamily: Font.family, fontSize: Font.sizeButton, color: Hex.textPrimary,
    }).setOrigin(0.5).setDepth(1);
    this.objects.push(attnLabel);

    const labelLeft = attnLabel.x - attnLabel.displayWidth / 2;
    const icon = this.add.image(labelLeft - iconGap - iconSize / 2, attnY, 'attunement-icons', `tree-icon-${treeName}`)
      .setDisplaySize(iconSize, iconSize)
      .setOrigin(0.5)
      .setDepth(1);
    this.objects.push(icon);
    const startX = cx - totalWidth / 2 + cardWidth / 2;
    const textWidth = cardWidth - CARD_PAD * 4;

    for (let i = 0; i < options.length; i++) {
      const optionId = options[i];
      const node = ConfigLoader.getV4Node(optionId);
      if (!node) continue;

      const cardX = startX + i * (cardWidth + CARD_GAP);
      const cardCY = cardTop + cardHeight / 2;

      // Card border — only drawn on hover
      const g = this.add.graphics().setDepth(1);
      this.objects.push(g);

      // Card background — entire card is clickable
      const optId = optionId;
      const card = crispRect(this, cardX, cardCY, cardWidth, cardHeight, Color.panelBg, 0.9)
        .setDepth(1)
        .setInteractive({ useHandCursor: true });
      if (this.mode === 'fork') {
        card.on('pointerdown', () => this.onChoose(optId));
      } else {
        card.on('pointerdown', () => this.dismiss());
      }
      card.on('pointerover', () => { g.clear(); g.lineStyle(3, Color.treeTierThreshold, 1.0); g.strokeRoundedRect(cardX - cardWidth / 2, cardTop, cardWidth, cardHeight, 3); });
      card.on('pointerout', () => { g.clear(); });
      this.objects.push(card);

      // Skill name
      const nameText = crispText(this, cardX, cardTop + CARD_PAD + 8, node.name.toUpperCase(), {
        fontFamily: Font.family, fontSize: Font.sizeSmall, color: Hex.textPrimary,
      }).setOrigin(0.5).setDepth(2);
      this.objects.push(nameText);

      // Type badge
      const typeLabel = NODE_TYPE_LABELS[node.nodeType] ?? node.nodeType;
      const typeText = crispText(this, cardX, cardTop + CARD_PAD + 22, typeLabel, {
        fontFamily: Font.family, fontSize: Font.sizeXs, color: Hex.blueBright,
      }).setOrigin(0.5).setDepth(2);
      this.objects.push(typeText);

      // Flavor text — centered, constrained to card width
      const flavorTop = cardTop + CARD_PAD + 36;
      const FLAVOR_DESC_GAP = 10;

      // wordWrap width must be in the text's internal coordinate space (CRISP_SCALE × logical)
      const wrapWidth = textWidth * CRISP_SCALE;

      let nextY = flavorTop;
      if (node.flavorText) {
        const flavor = crispText(this, cardX, flavorTop, node.flavorText, {
          fontFamily: Font.family, fontSize: Font.sizeTiny, color: Hex.textSecondary,
          wordWrap: { width: wrapWidth },
          align: 'center',
        }).setOrigin(0.5, 0).setDepth(2);
        this.objects.push(flavor);
        // Use actual rendered height (display scale already applied) + fixed gap
        nextY = flavorTop + flavor.displayHeight + FLAVOR_DESC_GAP;
      }

      // Description — try preferred size, fall back to smaller if it overflows
      const maxDescHeight = cardBottom - CARD_PAD - nextY;
      const descSizes = ['12px', '11px', '10px'];

      let desc!: Phaser.GameObjects.Text;
      for (const size of descSizes) {
        desc = crispText(this, cardX - textWidth / 2, nextY, node.description, {
          fontFamily: Font.family, fontSize: size, color: Hex.textMuted,
          wordWrap: { width: wrapWidth },
        }).setOrigin(0, 0).setDepth(2);
        if (desc.displayHeight <= maxDescHeight) break;
        desc.destroy();
      }
      // Final fallback: crop if even the smallest size overflows
      if (desc.displayHeight > maxDescHeight) {
        desc.setCrop(0, 0, wrapWidth, maxDescHeight * CRISP_SCALE);
      }
      this.objects.push(desc);

    }

    // ESC to dismiss
    this.input.keyboard!.on('keydown-ESC', () => this.dismiss());

    // B to cycle to next fork (debug testing — fork mode only)
    if (this.mode === 'fork') {
      this.input.keyboard!.on('keydown-B', () => this.cycleNext());
    }
  }

  /** Build the fork list on first use, then cycle to the next entry. */
  private cycleNext(): void {
    if (ForkChoiceScene.allForks.length === 0) {
      for (const [treeName, tree] of Object.entries(ConfigLoader.v4Trees)) {
        for (const fork of tree.forks) {
          ForkChoiceScene.allForks.push({ treeName, forkId: fork.forkId, options: [...fork.options] });
        }
      }
    }
    if (ForkChoiceScene.allForks.length === 0) return;
    ForkChoiceScene.forkIndex = (ForkChoiceScene.forkIndex + 1) % ForkChoiceScene.allForks.length;
    const next = ForkChoiceScene.allForks[ForkChoiceScene.forkIndex];
    // Restart this scene with the new fork data
    this.scene.start('ForkChoiceScene', next);
  }

  /**
   * Tears down the scene's objects and either launches the next queued attainment popup
   * or stops the scene and resumes gameplay if the queue is empty.
   */
  private dismiss(): void {
    for (const obj of this.objects) obj.destroy();
    this.objects = [];
    if (ForkChoiceScene.hasQueued()) {
      const next = ForkChoiceScene.dequeue()!;
      this.scene.start('ForkChoiceScene', { mode: 'attained', ...next });
    } else {
      this.scene.stop();
      this.scene.resume('GameScene');
    }
  }

  /** Commits the player's fork choice to SkillProgressionComponent, then dismisses the scene. */
  private onChoose(optionId: string): void {
    const gs = this.scene.get('GameScene') as GameScene;
    if (gs?.player) {
      gs.player.skillProgression.chooseFork(
        this.forkEvent.treeName,
        this.forkEvent.forkId,
        optionId,
      );
    }
    this.dismiss();
  }
}
